import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { SiPostgresql, SiElasticsearch, SiJira, SiGithub, SiGitlab, SiDatadog, SiSplunk, SiSentry, SiLinear } from "react-icons/si";
import { VscAzureDevops, VscAzure } from "react-icons/vsc";
import { FaAws, FaDatabase, FaCloud, FaSlack } from "react-icons/fa";
import { ArrowRight, Terminal, Cpu, GitBranch, Database, Clock, Sparkles, Lock, Zap, ChevronRight } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { useT } from "@/lib/i18n";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/223c22a7-431a-4bba-b000-82c6b7cf0541/images/b8654b91cc2bbbdf957a8f0827125c95671d1e602fed45272cdbf81fb4825e62.png";
const GRID_BG = "https://static.prod-images.emergentagent.com/jobs/223c22a7-431a-4bba-b000-82c6b7cf0541/images/efac1f07ac1e0449f2194b8df85821efaea81df6b39462087e5688eb07563bab.png";
const INFRA_IMG = "https://images.unsplash.com/photo-1680691257251-5fead813b73e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByYWNrJTIwZGF0YWNlbnRlcnxlbnwwfHx8fDE3Nzg4OTQwMzB8MA&ixlib=rb-4.1.0&q=85";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

export default function Landing() {
  const t = useT();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-background" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <motion.div
            initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.7 }}
            className="col-span-1 lg:col-span-8"
          >
            <div className="overline mb-6" data-testid="hero-overline">{t("landing.overline")}</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              {t("landing.hero.from")} <span className="font-mono font-normal text-white/40 line-through">{t("landing.hero.minutes30")}</span>
              <br />
              {t("landing.hero.to")} <span className="text-primary">{t("landing.hero.minutes2")}</span> {t("landing.hero.persupport")}
            </h1>
            <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">
              {t("landing.hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/signup" className="btn-primary" data-testid="hero-cta-signup">
                {t("landing.cta.signup")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/demo" className="btn-ghost" data-testid="hero-cta-demo">
                {t("landing.cta.demo")} <Terminal className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { k: "94%", v: t("landing.stat.lessTime") },
                { k: "10+", v: t("landing.stat.sources") },
                { k: "100%", v: t("landing.stat.audit") },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-mono text-3xl font-bold text-primary">{s.k}</div>
                  <div className="text-xs text-white/50 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Terminal preview */}
          <motion.div
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="col-span-1 lg:col-span-4 surface noise"
          >
            <div className="border-b border-white/10 px-4 py-2 flex items-center gap-2 bg-white/5">
              <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
              <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
              <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
              <div className="ml-3 font-mono text-[11px] text-white/50">ratchet · card-8821934</div>
            </div>
            <div className="p-4 font-mono text-xs leading-relaxed space-y-1.5">
              <div className="text-white/40">$ ratchet analyze --card 8821934</div>
              <div className="text-white/70">→ fetched card from Azure DevOps</div>
              <div className="text-white/70">→ extracted tenant_id=northwind-eu</div>
              <div className="text-white/70">→ queried DynamoDB (OrdersTable) <span className="text-emerald-400">ok</span></div>
              <div className="text-white/70">→ CloudWatch /aws/lambda/* <span className="text-amber-400">3 errors</span></div>
              <div className="text-white/70">→ Claude Sonnet correlating events...</div>
              <div className="text-emerald-400">▌ Root cause identified.</div>
              <div className="pt-2 text-white/40">────────────────────</div>
              <div className="text-white/60">IAM Role lambda-scheduleJob</div>
              <div className="text-white/60">missing sqs:SendMessage policy.</div>
              <div className="text-white/60">Posting to Azure DevOps... <span className="text-emerald-400">done</span></div>
              <div className="pt-2 text-primary">⏱ 1m 47s · saved ~28m</div>
            </div>
          </motion.div>
        </div>

        {/* Integrations marquee */}
        <div className="border-y border-white/5 bg-black/60" id="integrations">
          <div className="overline text-center py-4">{t("landing.connects")}</div>
          <div className="pb-6">
            <Marquee gradient gradientColor="#0A0A0A" gradientWidth={120} speed={40}>
              {[
                { Icon: VscAzureDevops, name: "Azure DevOps" },
                { Icon: SiJira, name: "Jira" },
                { Icon: SiGithub, name: "GitHub" },
                { Icon: SiGitlab, name: "GitLab" },
                { Icon: SiLinear, name: "Linear" },
                { Icon: FaAws, name: "AWS" },
                { Icon: FaDatabase, name: "DynamoDB" },
                { Icon: FaCloud, name: "CloudWatch" },
                { Icon: SiDatadog, name: "Datadog" },
                { Icon: SiSplunk, name: "Splunk" },
                { Icon: SiSentry, name: "Sentry" },
                { Icon: SiPostgresql, name: "PostgreSQL" },
                { Icon: SiElasticsearch, name: "Elasticsearch" },
                { Icon: VscAzure, name: "Azure" },
                { Icon: FaSlack, name: "Slack" },
              ].map(({ Icon, name }, i) => (
                <div key={i} className="flex items-center gap-3 mx-12 text-white/40 hover:text-white transition">
                  <Icon className="w-6 h-6" />
                  <span className="font-mono text-sm tracking-tight">{name}</span>
                </div>
              ))}
            </Marquee>
          </div>
          <div className="text-center pb-6">
            <Link to="/integrations" className="font-mono text-xs text-white/60 hover:text-white transition-colors" data-testid="landing-all-integrations">
              {t("landing.allIntegrations")} <ChevronRight className="inline w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-32 relative">
        <div className="absolute inset-0 -z-10 opacity-20" style={{ backgroundImage: `url(${GRID_BG})`, backgroundSize: 'cover' }} />
        <div className="overline mb-4">{t("landing.flow.overline")}</div>
        <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter max-w-3xl">
          {t("landing.flow.title")}
          <span className="text-white/40">{t("landing.flow.titleHL")}</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-16">
          {[
            { n: "01", Icon: GitBranch },
            { n: "02", Icon: Cpu },
            { n: "03", Icon: Sparkles },
            { n: "04", Icon: Database },
            { n: "05", Icon: Terminal },
            { n: "06", Icon: Zap },
            { n: "07", Icon: ChevronRight },
            { n: "08", Icon: ArrowRight },
            { n: "09", Icon: Sparkles },
          ].map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.04 }}
              className={`surface p-6 ${i % 4 === 0 ? "md:col-span-6" : "md:col-span-3"}`}
              data-testid={`step-${s.n}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="overline">{s.n}</div>
                <s.Icon className="w-4 h-4 text-primary" />
              </div>
              <h3 className="font-display font-bold text-xl mb-2">{t(`flow.${s.n}.title`)}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{t(`flow.${s.n}.desc`)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SOCIAL PROOF / VALUE BAND */}
      <section className="border-y border-white/5 bg-gradient-to-r from-black via-[#050A1A] to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { stat: "28 min", label: t("landing.proof.timeSaved"), note: t("landing.proof.timeSavedNote") },
            { stat: "1.4k+", label: t("landing.proof.cards"), note: t("landing.proof.cardsNote") },
            { stat: "$74k", label: t("landing.proof.cost"), note: t("landing.proof.costNote") },
          ].map((b, i) => (
            <div key={i} className="border-l border-white/10 pl-6">
              <div className="overline mb-3">{t("landing.proof.overline")} · 0{i+1}</div>
              <div className="font-display font-black text-5xl tracking-tighter text-white">{b.stat}</div>
              <div className="text-white/80 mt-2">{b.label}</div>
              <div className="text-xs text-white/40 font-mono mt-1">{b.note}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-32">
        <div className="overline mb-4">{t("landing.features.overline")}</div>
        <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter max-w-3xl">
          {t("landing.features.title")} <span className="text-white/40">{t("landing.features.titleHL")}</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-12">
          <div className="md:col-span-8 surface p-10 relative overflow-hidden">
            <div className="overline mb-3">{t("landing.features.multi.overline")}</div>
            <h3 className="font-display font-bold text-3xl mb-3">{t("landing.features.multi.title")}</h3>
            <p className="text-white/60 max-w-xl">{t("landing.features.multi.desc")}</p>
            <div className="mt-8 grid grid-cols-3 gap-3 font-mono text-xs">
              {["scheduled_job · 142", "duplicate_charge · 89", "api_timeout · 67"].map((c, i) => (
                <div key={i} className="border border-white/10 p-3 text-white/70">
                  <div className="text-primary text-lg">{c.split("·")[1]}</div>
                  <div className="opacity-60">{c.split("·")[0]}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-4 surface p-10">
            <Lock className="w-6 h-6 text-primary mb-4" />
            <h3 className="font-display font-bold text-2xl mb-2">{t("landing.features.creds.title")}</h3>
            <p className="text-white/60 text-sm">{t("landing.features.creds.desc")}</p>
          </div>
          <div className="md:col-span-4 surface p-10">
            <Clock className="w-6 h-6 text-primary mb-4" />
            <h3 className="font-display font-bold text-2xl mb-2">{t("landing.features.progressive.title")}</h3>
            <p className="text-white/60 text-sm">{t("landing.features.progressive.desc")}</p>
          </div>
          <div className="md:col-span-8 surface p-0 grid grid-cols-1 md:grid-cols-2 overflow-hidden">
            <div className="p-10">
              <div className="overline mb-3">{t("landing.features.kb.overline")}</div>
              <h3 className="font-display font-bold text-2xl mb-2">{t("landing.features.kb.title")}</h3>
              <p className="text-white/60 text-sm">{t("landing.features.kb.desc")}</p>
            </div>
            <div className="relative min-h-[200px]" style={{ backgroundImage: `url(${INFRA_IMG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F0F] via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 text-center">
        <div className="overline mb-4">{t("landing.pricing.overline")}</div>
        <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">{t("landing.pricing.title")}</h2>
        <p className="mt-4 text-white/60 max-w-xl mx-auto">{t("landing.pricing.subtitle")}</p>
        <div className="mt-10 flex justify-center gap-3">
          <Link to="/pricing" className="btn-primary" data-testid="cta-see-pricing">{t("landing.pricing.see")} <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/demo" className="btn-ghost">{t("landing.pricing.tryDemo")}</Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
